/**
 * Release a WebSocket a hook no longer owns without tripping Chromium's
 * "WebSocket is closed before the connection is established" console warning.
 *
 * Chromium logs that warning whenever `close()` is called on a socket whose
 * handshake has not finished. Every socket-backed hook here opens its
 * connection from a mount effect and closes it in the cleanup, so an unmount
 * inside the handshake window hits it.
 *
 * The common case is not handled here at all: React 18 StrictMode runs the
 * effect pair mount → cleanup → mount synchronously on every dev mount, so the
 * hooks schedule their first `connect()` on a zero-delay timer that the
 * cleanup cancels. StrictMode's throwaway mount therefore never constructs a
 * socket, and the server never sees it. That leaves this helper for a real
 * unmount (or bandId change) that lands inside the handshake window.
 *
 * A CONNECTING socket cannot be aborted silently, so instead of closing it now
 * this parks a one-shot `onopen` that closes it the moment the handshake
 * completes. Trade-offs of that choice, which callers should be aware of:
 *   - The server sees a complete connect/disconnect pair for the abandoned
 *     socket, where an aborted handshake may or may not have registered. For
 *     an attendance-tracking stream-status socket that is a momentary session
 *     (see useStreamStatus); for the band chat socket it is one extra
 *     connect/disconnect pair against the chat backend (see useBandChat).
 *   - A handshake that never completes (server accepts TCP but never upgrades)
 *     is no longer torn down here; the socket stays pending until the browser's
 *     own connect timeout releases it.
 * All other handlers are detached first so the abandoned socket cannot call
 * back into a hook whose effect has already been torn down: the reconnect /
 * polling fallback in `onclose` belongs to the mount that owned the socket,
 * not the one that replaced it.
 *
 * @param {WebSocket|null|undefined} ws
 */
export function closeSocketQuietly(ws) {
  if (!ws) return;

  ws.onmessage = null;
  ws.onerror = null;
  ws.onclose = null;

  if (ws.readyState === WebSocket.CONNECTING) {
    ws.onopen = () => ws.close();
    return;
  }

  ws.onopen = null;
  if (ws.readyState === WebSocket.OPEN) {
    ws.close();
  }
}
